import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import { useNavigate } from 'react-router-dom';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const DashboardTripCountTotalHrs = ({
    vehicleTypeId = null,
    vendorId = null,
    driverId = null,
    vehicleId = null,
    // period = '3m'
    period = null
}) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedRange, setSelectedRange] = useState(period);
    const [chartData, setChartData] = useState({});

    const navigate = useNavigate();

    const monthNames = [
        '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    useEffect(() => {
        fetchData();
    }, [vehicleTypeId, vendorId, driverId, vehicleId, selectedRange, period]);

    const fetchData = async () => {
        const userId = localStorage.getItem('userId');
        const authToken = localStorage.getItem('token');

        if (!userId || !authToken) {
            toast.error('Missing userId or Authorization token in localStorage');
            return;
        }

        try {
            const response = await fetch(`${ApiBaseURL}/dashboard/v1/get/trip-count-monthwise`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    userId,
                    Authorization: `Bearer ${authToken}`,
                },
                body: JSON.stringify({
                    vehicleTypeId,
                    vendorId,
                    driverId,
                    vehicleId,
                    // period: selectedRange
                    period
                })
            });

            const result = await response.json();

            if (response.ok && result.statusDescription.statusCode === 200) {
                setChartData(result.data || {});
            } else if ([401, 403].includes(result.statusDescription.statusCode)) {
                toast.error(result.statusDescription.description || "Unauthorized access. Redirecting...");
                localStorage.clear();

                setTimeout(() => {
                    window.location.replace('/');
                }, 1500);
            } else {
                console.log(result.statusDescription.description || 'Failed to fetch trip count data');
            }
        } catch (error) {
            toast.error('Error fetching trip count data: ' + error.message);
        }
    };

    const parsedData = Object.entries(chartData)
        .map(([key, item]) => {
            const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);
            const label = isDateKey
                ? new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                : monthNames[parseInt(key)] || '';

            return {
                label,
                sortKey: isDateKey ? new Date(key).getTime() : parseInt(key),
                tripCount: item.tripCount || 0,
                compositeTripCount: item.composite || 0,
                reHandlingTripCount: item.rehandling || 0,
            };
        })
        .sort((a, b) => a.sortKey - b.sortKey);

    // const parsedData = Object.entries(chartData)
    //     .map(([key, item]) => {
    //         const isDateKey = /^\d{4}-\d{2}-\d{2}$/.test(key);

    //         let label = '';

    //         if (isDateKey) {
    //             const date = new Date(key);
    //             const month = date.toLocaleString('en-GB', { month: 'short' }); // e.g., "Jun"
    //             const yearShort = date.getFullYear().toString().slice(-2);      // e.g., "25"
    //             label = `${month}-${yearShort}`;                                // "Jun-25"
    //         } else {
    //             // key is numeric month (like "6" or "06")
    //             const monthIndex = parseInt(key, 10) - 1; // JS months are 0-based
    //             const month = monthNames[monthIndex] || '';
    //             const yearShort = '25'; // fixed year, change as needed
    //             label = `${month}-${yearShort}`; // e.g., "Jun-25"
    //         }

    //         return {
    //             label,
    //             sortKey: isDateKey ? new Date(key).getTime() : parseInt(key),
    //             tripCount: item.tripCount || 0,
    //             compositeTripCount: item.composite || 0,
    //             reHandlingTripCount: item.rehandling || 0,
    //         };
    //     })
    //     .sort((a, b) => a.sortKey - b.sortKey);

    const labels = parsedData.map(item => item.label);

    const data = {
        labels,
        datasets: [
            {
                label: `Trips`,
                data: parsedData.map(item => item.tripCount),
                // borderColor: 'rgb(5, 47, 47)',
                // backgroundColor: 'rgba(75, 192, 192, 0.2)',
                borderColor: '#4B81FA',
                backgroundColor: '#4B81FA',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: `Composite Trips`,
                data: parsedData.map(item => item.compositeTripCount),
                borderColor: '#005E00',
                backgroundColor: '#005E00',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: `Rehandling Trips`,
                data: parsedData.map(item => item.reHandlingTripCount),
                borderColor: '#D10895',
                backgroundColor: '#D10895',
                tension: 0.4,
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: {
                position: 'top'
            },
            title: {
                display: true,
                // text: 'Trip vs Composite vs Rehandling Counts (Monthly)'
            }
        },
        scales: {
            y: {
                ticks: {
                    callback: function (value) {
                        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
                        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1).replace(/\.0$/, '') + 'L';
                        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
                        return value;
                    }
                }
            }
        }
    };

    const filterOptions = [
        { label: 'Year-25', value: 'y-25' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Week', value: 'week' },
        { label: 'Current Month', value: 'cm' },
        { label: 'Prev. Month', value: 'pm' },
        { label: '3M', value: '3m' },
        { label: '6M', value: '6m' },
        { label: '9M', value: '9m' },
    ];

    const handleFilterSelect = (range) => {
        setSelectedRange(range);
        setShowDropdown(false);
    };

    const totals = useMemo(() => {
        return Object.values(chartData).reduce(
            (acc, item) => {
                acc.tripCount += item.tripCount || 0;
                acc.composite += item.composite || 0;
                acc.rehandling += item.rehandling || 0;
                return acc;
            },
            { tripCount: 0, composite: 0, rehandling: 0 }
        );
    }, [chartData]);

    return (
        <div className="row g-0">
            <div className="card shadow-sm my-0">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h5 className="card-title mb-0">Trip Analysis</h5>
                        <div style={{ position: 'relative' }}>
                            <i
                                className="fa-solid fa-filter d-none"
                                style={{ cursor: 'pointer', fontSize: '18px' }}
                                onClick={() => setShowDropdown(!showDropdown)}
                            ></i>
                            {showDropdown && (
                                <div
                                    className="dropdown-menu show"
                                    style={{ position: 'absolute', right: 0, zIndex: 999 }}
                                >
                                    {filterOptions.map(({ label, value }) => (
                                        <button
                                            key={value}
                                            className="dropdown-item"
                                            onClick={() => handleFilterSelect(value)}
                                        >
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    <Bar data={data} options={options} />
                </div>

                <div className="row mb-3 text-center">
                    <div className="col-md-4">
                        <h6 className="mb-1">Total Trips</h6>
                        <p className="fw-bold text-primary mb-0">
                            {new Intl.NumberFormat('en-IN').format(Math.round(totals.tripCount))}
                        </p>
                    </div>

                    <div className="col-md-4">
                        <h6 className="mb-1">Composite Trips</h6>
                        <p className="fw-bold text-success mb-0">
                            {new Intl.NumberFormat('en-IN').format(Math.round(totals.composite))}
                        </p>
                    </div>

                    <div className="col-md-4">
                        <h6 className="mb-1">Rehandling Trips</h6>
                        <p className="fw-bold mb-0" style={{ color: "#D10895" }}>
                            {new Intl.NumberFormat('en-IN').format(Math.round(totals.rehandling))}
                        </p>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default DashboardTripCountTotalHrs;